'use client'

import React, { createContext, useState, useContext, useEffect } from 'react'
import { useAuth } from './AuthContext'
import { api } from '../configs/api'

type CompanyDataType = {
    id: string
    name: string
    image?: string
    situation?: string
    email?: string
    phone?: string
}

export type CompanyDetailType = {
    company: {
        id: number
        name: string
        image: string | null
        email: string
        phone: string
    }
    contacts: {
        id: number
        name: string
        image: string | null
        email: string
        phone: string
    }[]
    notes: {
        id: number
        businessID: number
        name: string
        date: string
        dateNotify: string | null
        description: string
        users: {
            id: number
            name: string
        }[]
    }[]
    business: {
        id: number
        pipeline: string
        step: string
        color: string
        description: string | null
        products: {
            id: number
            name: string
        }[]
        targets: {
            id: number
            name: string
            color: string
        }[]
    }[]
}

type CompanyType = {
    companies: CompanyDataType[]
    loadCompanies: () => Promise<void>
    setCompanies: (value: CompanyDataType[]) => void
    detail: CompanyDetailType | null
    getCompany: (id: string) => Promise<void>
    filteredCompanies: CompanyDataType[]
    setFilteredCompanies: (value: CompanyDataType[]) => void
}

const Company = createContext<CompanyType | undefined>(undefined)

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { unity } = useAuth()
    const [companies, setCompanies] = useState<CompanyDataType[]>([])
    const [detail, setDetail] = useState<CompanyDetailType | null>(null)
    const [filteredCompanies, setFilteredCompanies] = useState<CompanyDataType[]>([])

    const loadCompanies = async () => {
        if (!unity) return

        const response = await fetch(`${api}company/getData`, {
            method: 'POST',
            body: JSON.stringify({
                unityID: unity.unityID
            })
        })
        const result = await response.json()

        const formattedRows = result.companies.map((row: any) => ({
            ...row,
            email: row.email && row.email != '' ? row.email : '--',
            phone: row.phone && row.phone != '' ? row.phone : '--'
        }))

        setCompanies(formattedRows)
        setFilteredCompanies(formattedRows)
    }

    const getCompany = async (id: string) => {
        const response = await fetch(`${api}company/getDetail`, {
            method: 'POST',
            body: JSON.stringify({ id })
        })
        const result = await response.json()
        setDetail(result.detail)
    }

    useEffect(() => {
        //* Carrega empresas
        loadCompanies()
    }, [unity])

    return (
        <Company.Provider
            value={{
                companies,
                loadCompanies,
                setCompanies,
                detail,
                getCompany,
                filteredCompanies,
                setFilteredCompanies
            }}
        >
            {children}
        </Company.Provider>
    )
}

export function useCompany() {
    const context = useContext(Company)
    if (context === undefined) {
        throw new Error('useCreate must be used within a CompanyProvider')
    }
    return context
}
